/**
 * EARTH LAB — chapter 1 as an instrument, with the source underneath rather than in front.
 *
 * FOUNDER, 2026-08-10: "don't make latitude/longitude primarily a wall of text with coordinates…
 * A cadet can read 'the longitude is the arc of the Equator intercepted between the Prime Meridian…'
 * three times and still not see it. A rotatable globe makes that sentence obvious in seconds."
 *
 * And the structure he set, which this file implements in order:
 *
 *     SEE → MOVE → NAME → CALCULATE → APPLY → SOURCE
 *
 * rather than SOURCE → SOURCE → SOURCE → quiz.
 *
 * ── WHAT IS DELIBERATELY NOT REMOVED ──────────────────────────────────────────────────────────
 * "do not eliminate coordinates or the original source material. A Second Mate absolutely has to
 * become fluent in written coordinates, degrees/minutes, DLat/DLong, chart notation."
 *
 * So the readout is always in proper notation — 51° 27.4' N — and it updates while he drags, which
 * is the point: the notation becomes the LANGUAGE for a geometry he has already felt, instead of a
 * code he is asked to decode cold. Bowditch stays, cited, below the lab.
 *
 * ── THE LAB IS PAPER ──────────────────────────────────────────────────────────────────────────
 * The globe is the subject, so it does not follow the theme (paper/chrome ruling). Ocean stays
 * ocean.
 */

import { useState } from 'react';
import NavSphere, { dm } from './navsphere.jsx';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited } from '../../../packages/ankr-provenance/src/provenance.mjs';

/* The tasks, in the order the founder set them: place a given position, then read an unknown one.
   Placing first is deliberate — it is easier, and it builds the mental model the reading step then
   tests. Reversing that order teaches decoding before geometry, which is the habit being replaced. */
const TASKS = [
  { id: 'place-30n-45w', kind: 'place', lat: 30, lon: -45, tol: 6,
    ask: 'Move the vessel to approximately 30° N, 045° W.',
    why: 'North of the Equator and west of Greenwich — mid-Atlantic.' },
  { id: 'place-35s-020e', kind: 'place', lat: -35, lon: 20, tol: 6,
    ask: 'Now take her to approximately 35° S, 020° E.',
    why: 'South and east: off the Cape. Both hemispheres change at once.' },
  { id: 'read', kind: 'read', lat: 51.4567, lon: -1.31,
    ask: 'The vessel has been placed for you. Estimate her latitude and longitude, then check.',
    why: 'Reading a position off the geometry is the skill a chart demands.' },
];

export default function EarthLab() {
  const [pos, setPos] = useState({ lat: 20, lon: -30 });
  const [show, setShow] = useState({ equator: true, primeMeridian: true, arcs: true,
                                     parallels: false, convergence: false });
  const [t, setT] = useState(0);
  const [checked, setChecked] = useState(null);
  const task = TASKS[t];

  const start = (i) => {
    setT(i); setChecked(null);
    // A READ task places the vessel FOR him; a PLACE task must not move it to the answer.
    if (TASKS[i].kind === 'read') setPos({ lat: TASKS[i].lat, lon: TASKS[i].lon });
  };

  const check = () => {
    if (task.kind === 'place') {
      const dLat = Math.abs(pos.lat - task.lat);
      // Longitude difference must wrap: 179°E and 179°W are 2° apart, not 358°.
      const dLon = Math.abs(((pos.lon - task.lon + 540) % 360) - 180);
      setChecked({ ok: dLat <= task.tol && dLon <= task.tol, dLat, dLon });
    } else {
      setChecked({ reveal: true });
    }
  };

  const toggle = (k) => setShow((s) => ({ ...s, [k]: !s[k] }));

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Earth lab · move her yourself</div>
      <h2 className="g-h2 lab-h">Latitude and longitude, before the words</h2>
      <p className="g-body">
        Drag the vessel. Drag anywhere else to turn the Earth. The two arcs are the answer:
        <b className="lab-key lab-lat"> latitude</b> runs up the meridian from the Equator,
        <b className="lab-key lab-lon"> longitude</b> runs along the Equator from Greenwich.
      </p>

      <div className="lab-globe">
        <NavSphere height={300} overlays={show}
          marks={[{ lat: pos.lat, lon: pos.lon, label: 'vessel', arcs: show.arcs }]}
          onMove={(lat, lon) => { setPos({ lat, lon }); setChecked(null); }} />
      </div>

      {/* THE READOUT, always in the notation he must become fluent in — updating as he drags, so the
          numbers are a description of something he is doing rather than a code to decode. */}
      <div className="lab-readout">
        <div><span className="g-label">Latitude</span>
             <span className="lab-num lab-lat">{dm(pos.lat, 'lat')}</span></div>
        <div><span className="g-label">Longitude</span>
             <span className="lab-num lab-lon">{dm(pos.lon, 'lon')}</span></div>
      </div>

      <div className="shell-picker lab-toggles">
        {[['equator', 'Equator'], ['primeMeridian', 'Prime meridian'],
          ['parallels', 'Tropics & circles'], ['arcs', 'The two arcs'],
          ['convergence', 'A degree of longitude']].map(([k, l]) => (
          <button key={k} onClick={() => toggle(k)}
            className={`g-btn ${show[k] ? 'g-btn-primary' : ''}`}>{l}</button>
        ))}
      </div>

      <div className="lab-task">
        <div className="lab-task-head">
          <span className="g-label">Task {t + 1} of {TASKS.length}</span>
          <div className="shell-picker">
            {TASKS.map((x, i) => (
              <button key={x.id} onClick={() => start(i)}
                className={`g-btn ${i === t ? 'g-btn-primary' : ''}`}>{i + 1}</button>
            ))}
          </div>
        </div>
        <p className="g-body lab-ask">{task.ask}</p>

        <button className="g-btn g-btn-primary lab-check" onClick={check}>
          {task.kind === 'place' ? 'Check my position' : 'Show her position'}
        </button>

        {checked && task.kind === 'place' && (
          <div className="lab-result">
            <Value of={computed(checked.ok
              ? `Within ${task.tol}° on both — that is the position.`
              : `Out by ${checked.dLat.toFixed(1)}° of latitude and ${checked.dLon.toFixed(1)}° of ` +
                `longitude. Watch which arc has to grow.`)} />
            <p className="g-body lab-why">{task.why}</p>
          </div>
        )}
        {checked && task.kind === 'read' && (
          <div className="lab-result">
            <Value of={computed(`${dm(task.lat, 'lat')}   ${dm(task.lon, 'lon')}`)} />
            <p className="g-body lab-why">{task.why}</p>
          </div>
        )}
      </div>

      {/* THE CLAIM BOWDITCH MAKES THAT THE SPHERE WAS NOT SHOWING. Founder, reading chapter 1:
          "bowditch mentions something which doesn't appear on sphere". He was right, and it is the
          most load-bearing sentence in the passage — departure, the latitude scale, and Mercator all
          rest on it. Now it has a switch, and the numbers beside each arc are computed from the
          geometry rather than quoted from a table. */}
      {show.convergence && (
        <div className="lab-task">
          <div className="g-label">A degree of longitude is not a fixed distance</div>
          <div className="lab-result">
            <Value of={computed(
              'Each blue arc spans the same 15° of longitude, and each is shorter than the one below '
              + 'it: 900 nm at the Equator, 779 at 30°, 450 at 60°, 233 at 75°. The meridians are '
              + 'closing on each other. A degree of LATITUDE never changes — which is why distance is '
              + 'measured up the side of a chart and never along the bottom.')} />
          </div>
          <p className="g-body lab-why">
            This is departure: <b>dep = dLong × cos(lat)</b>. The cosine in that formula is the
            closing you are looking at, and it is the same fact the next chapter shows again when the
            chart has to stretch to keep the meridians parallel.
          </p>
        </div>
      )}

      {/* The definition arrives AFTER he has moved her — which is the whole reordering. */}
      <div className="lab-def">
        <Value of={cited(
          'The Longitude of a place on the surface of the earth is the arc of the Equator intercepted '
          + 'between the Prime Meridian and the meridian of the place. The Latitude of a place is the '
          + 'arc of the meridian intercepted between the Equator and the place.',
          'Bowditch, The American Practical Navigator (1888) — public domain')} />
        <p className="g-body lab-why">
          Read that again now. The two arcs above are the two arcs in the sentence.
        </p>
      </div>
    </div>
  );
}
